import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, ArrowUpDown, Download, Loader2, Search, Truck } from 'lucide-react';
import ReportDownloadLog from '@/components/ReportDownloadLog';
import { CsvDownloadButton } from '@/components/CsvDownloadButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 50;

const DATE_PRESET_OPTIONS = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'this_month', label: 'This month' },
  { value: 'custom', label: 'Custom range' },
];

const toISO = (d) => d.toISOString().slice(0, 10);

const presetToRange = (preset) => {
  const now = new Date();
  const today = toISO(now);
  if (preset === 'today') return { from: today, to: today };
  if (preset === 'yesterday') {
    const y = new Date(now); y.setDate(y.getDate() - 1);
    return { from: toISO(y), to: toISO(y) };
  }
  if (preset === 'this_week') {
    const d = new Date(now);
    const day = (d.getDay() + 6) % 7; // Monday=0
    d.setDate(d.getDate() - day);
    return { from: toISO(d), to: today };
  }
  if (preset === 'this_month') {
    return { from: `${today.slice(0, 7)}-01`, to: today };
  }
  return { from: '', to: '' };
};

const fmtINR = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

const fmtQty = (n) =>
  Number.isFinite(Number(n)) ? Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 }) : '0';

const numOrDash = (n) => (n === null || n === undefined || n === '' ? '—' : fmtINR(n));

const KPI = ({ label, value, accent, testid }) => (
  <Card className={`border-2 ${accent}`} data-testid={testid}>
    <CardContent className="p-4">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium leading-tight">{label}</div>
      <div className="text-xl font-bold text-slate-900 mt-1">{value}</div>
    </CardContent>
  </Card>
);

const COLUMNS = [
  { key: 'grn_number', label: 'GRN #' },
  { key: 'received_date', label: 'Received' },
  { key: 'status', label: 'Status' },
  { key: 'po_number', label: 'PO #' },
  { key: 'po_raised_date', label: 'PO Raised Date' },
  { key: 'po_raised_by', label: 'PO Raised By' },
  { key: 'supplier_name', label: 'Supplier' },
  { key: 'invoice_number', label: 'Invoice #' },
  { key: 'invoice_date', label: 'Invoice Date' },
  { key: 'invoice_value', label: 'Invoice Value', num: true },
  { key: 'product_name', label: 'Product' },
  { key: 'sku', label: 'SKU' },
  { key: 'batch_number', label: 'Batch' },
  { key: 'ordered_qty', label: 'Ordered', num: true, qty: true },
  { key: 'received_qty', label: 'Received', num: true, qty: true },
  { key: 'accepted_qty', label: 'Accepted', num: true, qty: true },
  { key: 'damaged_qty', label: 'Damaged', num: true, qty: true },
  { key: 'shortage_qty', label: 'Shortage', num: true, qty: true },
  { key: 'po_mrp', label: 'PO MRP', num: true },
  { key: 'grn_mrp', label: 'GRN MRP', num: true },
  { key: 'po_price_incl_gst', label: 'PO Price Incl GST', num: true },
  { key: 'grn_price_incl_gst', label: 'GRN Price Incl GST', num: true },
  { key: 'gst_percentage', label: 'GST %', num: true, qty: true },
];

export default function AllGRNReport() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [totalGrns, setTotalGrns] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [logRefresh, setLogRefresh] = useState(0);

  // filters
  const [datePreset, setDatePreset] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // sorting
  const [sortBy, setSortBy] = useState('received_date');
  const [sortDir, setSortDir] = useState('desc');

  const dateRange = useMemo(() => {
    if (datePreset === 'custom') return { from: customFrom, to: customTo };
    return presetToRange(datePreset);
  }, [datePreset, customFrom, customTo]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setLoading(true);
    const params = {};
    if (dateRange.from) params.date_from = dateRange.from;
    if (dateRange.to) params.date_to = dateRange.to;
    axios
      .get(`${API}/reports/all-grn/preview`, { params, headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setRows(res.data?.rows || []);
        setTotalGrns(res.data?.total_grns ?? res.data?.grn_count ?? 0);
        setPage(1);
      })
      .catch(() => toast.error('Failed to load GRN report preview'))
      .finally(() => setLoading(false));
  }, [dateRange.from, dateRange.to]);

  const statusOptions = useMemo(
    () => [...new Set(rows.map((r) => r.status).filter(Boolean))].sort(),
    [rows]
  );
  const supplierOptions = useMemo(
    () => [...new Set(rows.map((r) => r.supplier_name).filter(Boolean))].sort(),
    [rows]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = rows;
    if (statusFilter !== 'all') out = out.filter((r) => r.status === statusFilter);
    if (supplierFilter !== 'all') out = out.filter((r) => r.supplier_name === supplierFilter);
    if (q) {
      out = out.filter((r) =>
        ['grn_number', 'po_number', 'supplier_name', 'invoice_number', 'product_name', 'sku', 'batch_number']
          .some((k) => String(r[k] || '').toLowerCase().includes(q))
      );
    }
    return out;
  }, [rows, search, statusFilter, supplierFilter]);

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sortBy);
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = a[sortBy]; const bv = b[sortBy];
      if (col?.num) return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
      return String(av || '').localeCompare(String(bv || '')) * dir;
    });
  }, [filtered, sortBy, sortDir]);

  // ── KPI dashboard (deduped per GRN for header-level money fields) ──
  const kpis = useMemo(() => {
    const perGrn = {};
    let qtyReceived = 0;
    const skus = new Set();
    const suppliers = new Set();
    filtered.forEach((r) => {
      qtyReceived += Number(r.received_qty) || 0;
      if (r.sku) skus.add(r.sku);
      if (r.supplier_name) suppliers.add(r.supplier_name);
      if (r.grn_number && !(r.grn_number in perGrn)) {
        perGrn[r.grn_number] = {
          taxable: Number(r.grn_taxable_value) || 0,
          gst: Number(r.grn_gst_total) || 0,
          invoice: Number(r.invoice_value) || 0,
        };
      }
    });
    const vals = Object.values(perGrn);
    return {
      totalGrns: vals.length,
      totalLines: filtered.length,
      qtyReceived,
      taxable: vals.reduce((a, v) => a + v.taxable, 0),
      gst: vals.reduce((a, v) => a + v.gst, 0),
      invoiceValue: vals.reduce((a, v) => a + v.invoice, 0),
      suppliers: suppliers.size,
      skus: skus.size,
    };
  }, [filtered]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = sorted.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(key); setSortDir('desc'); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      if (dateRange.from) params.date_from = dateRange.from;
      if (dateRange.to) params.date_to = dateRange.to;
      const res = await axios.get(`${API}/reports/all-grn.xlsx`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `all_grn_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('All GRN report exported');
      setLogRefresh((n) => n + 1);
    } catch {
      toast.error('Failed to download All GRN report');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="all-grn-report-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/reports')} data-testid="all-grn-back-btn">
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Truck className="text-emerald-600" size={26} /> All GRN Report
          </h1>
        </div>
        <Button
          onClick={handleDownload}
          disabled={downloading || loading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
          data-testid="all-grn-download-btn"
        >
          {downloading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          {downloading ? 'Preparing…' : 'Download Excel'}
        </Button>
        <CsvDownloadButton onDownload={handleDownload} testId="all-grn-download-csv" size="default" disabled={loading} />
      </div>

      {/* ── Filters ─────────────────────────────────────────── */}
      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-40">
              <Label className="text-xs text-slate-500">Date Range</Label>
              <Select value={datePreset} onValueChange={(v) => { setDatePreset(v); setPage(1); }}>
                <SelectTrigger className="h-9 mt-1" data-testid="all-grn-date-preset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_PRESET_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`date-preset-${o.value}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {datePreset === 'custom' && (
              <>
                <div>
                  <Label className="text-xs text-slate-500">From</Label>
                  <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                    className="h-9 mt-1 w-40" data-testid="all-grn-date-from" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">To</Label>
                  <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                    className="h-9 mt-1 w-40" data-testid="all-grn-date-to" />
                </div>
              </>
            )}
            <div className="w-40">
              <Label className="text-xs text-slate-500">Status</Label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="h-9 mt-1" data-testid="all-grn-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-52">
              <Label className="text-xs text-slate-500">Supplier</Label>
              <Select value={supplierFilter} onValueChange={(v) => { setSupplierFilter(v); setPage(1); }}>
                <SelectTrigger className="h-9 mt-1" data-testid="all-grn-supplier-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All suppliers</SelectItem>
                  {supplierOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[220px]">
              <Label className="text-xs text-slate-500">Search</Label>
              <div className="relative mt-1">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                  placeholder="GRN, PO, supplier, invoice, product, batch…"
                  className="pl-9 h-9"
                  data-testid="all-grn-search-input"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── KPI dashboard ───────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Total GRNs" value={loading ? '…' : kpis.totalGrns} accent="border-emerald-200 bg-emerald-50/50" testid="kpi-total-grns" />
        <KPI label="Total GRN Lines / Items" value={loading ? '…' : kpis.totalLines} accent="border-teal-200 bg-teal-50/50" testid="kpi-total-lines" />
        <KPI label="Total Qty Received" value={loading ? '…' : fmtQty(kpis.qtyReceived)} accent="border-sky-200 bg-sky-50/50" testid="kpi-qty-received" />
        <KPI label="Total Taxable Value" value={loading ? '…' : `₹${fmtINR(kpis.taxable)}`} accent="border-indigo-200 bg-indigo-50/50" testid="kpi-taxable-value" />
        <KPI label="Total GST" value={loading ? '…' : `₹${fmtINR(kpis.gst)}`} accent="border-violet-200 bg-violet-50/50" testid="kpi-total-gst" />
        <KPI label="Total Invoice Value" value={loading ? '…' : `₹${fmtINR(kpis.invoiceValue)}`} accent="border-amber-200 bg-amber-50/50" testid="kpi-invoice-value" />
        <KPI label="Total Suppliers" value={loading ? '…' : kpis.suppliers} accent="border-rose-200 bg-rose-50/50" testid="kpi-total-suppliers" />
        <KPI label="Total SKUs Received" value={loading ? '…' : kpis.skus} accent="border-lime-200 bg-lime-50/50" testid="kpi-total-skus" />
      </div>

      {/* ── Preview table ───────────────────────────────────── */}
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-4">
          <div className="text-xs text-slate-500 text-right">
            Showing {pageRows.length} of {sorted.length} rows · Page {safePage}/{totalPages}
            {totalGrns ? ` · ${totalGrns} GRNs all-time` : ''}
          </div>
          <div className="overflow-x-auto border rounded-lg">
            <table className="min-w-full text-xs" data-testid="all-grn-table">
              <thead className="bg-slate-100 text-slate-600 uppercase tracking-wide">
                <tr>
                  {COLUMNS.map((c) => (
                    <th
                      key={c.key}
                      onClick={() => toggleSort(c.key)}
                      className={`px-3 py-2 font-semibold whitespace-nowrap cursor-pointer select-none hover:text-slate-900 ${c.num ? 'text-right' : 'text-left'}`}
                      data-testid={`all-grn-sort-${c.key}`}
                    >
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        <ArrowUpDown size={11} className={sortBy === c.key ? 'text-emerald-600' : 'text-slate-400'} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={COLUMNS.length} className="px-3 py-10 text-center text-slate-400">
                    <Loader2 size={18} className="animate-spin inline mr-2" /> Loading report…
                  </td></tr>
                ) : pageRows.length === 0 ? (
                  <tr><td colSpan={COLUMNS.length} className="px-3 py-10 text-center text-slate-400">No GRN rows match the current filters</td></tr>
                ) : (
                  pageRows.map((r, i) => (
                    <tr key={`${r.grn_number}-${i}`} className="hover:bg-emerald-50/40" data-testid="all-grn-row">
                      {COLUMNS.map((c) => (
                        <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.num ? 'text-right tabular-nums' : 'text-left'}`}>
                          {c.qty
                            ? (r[c.key] ?? 0)
                            : c.num
                              ? numOrDash(r[c.key])
                              : (r[c.key] || '—')}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" disabled={safePage <= 1} onClick={() => setPage(safePage - 1)} data-testid="all-grn-prev-page">
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)} data-testid="all-grn-next-page">
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ReportDownloadLog reportKey="all-grn" refreshToken={logRefresh} />
    </div>
  );
}
